import { useQuery } from '@tanstack/react-query';
import { PkButton, PkIcon, PkListStatItem } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useMemo } from 'react';

import { withErrorBoundary } from '../ErrorBoundary';
import { useStatDefinitions } from '../hooks/useStatDefinitions';
import PlayerIdentifier from '../Identifier/PlayerIdentifier';
import PictureHelper from '../PictureHelper';
import { getMatchStatsQuery } from '../queries/statistics';
import RandomSponsors from '../RandomSponsors';
import { useTranslateFn } from '../Translate';
import VideoPlayer from '../VideoPlayer';

import { useArticle } from './hooks/useArticle';

import { trackCardElement } from '@services/GoogleTagManagerService';
import { getPlayerUrl } from '@services/LinkRuleService';
import { useSponsor } from '@shared/RandomSponsors/useSponsor';
import type { Match } from '@shared-types/match.types';
import { IS_DEBUG, IS_TEST } from '@utilities/browser';
import { getRefreshRate } from '@utilities/live';
import { getStatNumericValue, getStatUnitMeasureLabel } from '@utilities/stats';

import * as classes from './styles.module.scss';

const imgFormat = ['0@s1'];

const PotmCard = ({
  match,
  statTypes,
  isFemaleCompetition,
}: {
  match: Match;
  statTypes: { [key: string]: string[] };
  isFemaleCompetition: boolean;
}) => {
  const t = useTranslateFn();

  const refreshRate = useMemo(
    () =>
      getRefreshRate(match, {
        LIVE: 30 * 1000, // 30 seconds (status LIVE or <3h before/after match)
        NEAR: -1, // polling disabled (same day)
        DEFAULT: -1, // polling disabled
      }),
    [match],
  );

  const { player, teamId } = useMemo(() => match.playerOfTheMatch ?? {}, [match.playerOfTheMatch]);
  const team = useMemo(
    () => (match.homeTeam?.id === teamId ? match.homeTeam : match.awayTeam?.id === teamId ? match.awayTeam : null),
    [match.awayTeam, match.homeTeam, teamId],
  );
  const article = useArticle(match, player, refreshRate);

  const statTypesByRole = useMemo(
    () => statTypes?.[player?.fieldPosition.toLowerCase()] ?? statTypes?.unknown ?? [],
    [player?.fieldPosition, statTypes],
  );

  const statDefs = useStatDefinitions(statTypesByRole);
  const { data: stats } = useQuery({
    ...getMatchStatsQuery(match.id, player?.id),
    refetchInterval: !IS_TEST && refreshRate,
  });

  const hasMedia = useMemo(() => !!article?.mainMedia, [article]);
  const hasVideo = useMemo(() => article?.mainMedia?.type === 'videostory', [article]);
  const { sponsor, loaded } = useSponsor(article?.sponsor?.type ?? 'potm');

  const trackingData = useMemo(
    () =>
      loaded && {
        category: 'sponsored card',
        id: match.id,
        name: `Player of the match - ${player?.translations.name.EN}`,
        item_category3: sponsor ? 'sponsored' : undefined,
        item_category4: sponsor ? sponsor?.code : undefined,
      },
    [sponsor, player?.translations.name.EN, match.id, loaded],
  );

  const enableLink = useMemo(() => +match.seasonYear >= window.currentSeason, [match.seasonYear]);

  if (!player?.id) return null;

  return (
    <div ref={trackCardElement(trackingData)} pk-theme='dark' className={classes.wrapper}>
      <div className={classes.container}>
        <div
          className={clsx({
            [classes.topPart]: true,
            [classes.topPartNoMedia]: !hasMedia,
            [classes.topPartNoStats]: !stats,
          })}
        >
          <div className={classes.topPartSpacer} />
          <div className={classes.topPartContent}>
            <div className={classes.header}>
              <div className={classes.title}>{t(`player_of_the_match${isFemaleCompetition ? '_f' : ''}`)}</div>
              <div className={classes.sponsor}>
                <RandomSponsors
                  extract={1}
                  sponsorName={article?.sponsor?.type ?? 'potm'}
                  position='card_liveblog'
                  creative='branded-card'
                />
              </div>
            </div>
            {hasMedia && (
              <>
                {hasVideo ? (
                  <VideoPlayer
                    isAutoPlay
                    isMuted
                    repeat
                    wrapperClassName={classes.video}
                    aspectratio='1:1'
                    stretching='fill'
                    showTitle={false}
                    teaser={article.mainMedia}
                  />
                ) : (
                  <PictureHelper wrapperClassName={classes.photo} formats={imgFormat} image={article.mainMedia.image} />
                )}
              </>
            )}
            <div className={classes.identifier}>
              <PlayerIdentifier
                team={team}
                player={player}
                type={hasMedia ? 'horizontal' : 'vertical'}
                enableLink={enableLink}
                useFullName
                showPosition={IS_DEBUG}
                avatarSize={hasMedia ? 40 : 74}
                badgeSize={hasMedia ? 13 : 25}
                phase={match?.round?.phase}
              />
            </div>
          </div>
        </div>
        {stats && (
          <div className={classes.stats}>
            {statDefs.map((s) => {
              const value = getStatNumericValue(stats, s);
              return (
                value != null && (
                  <PkListStatItem
                    key={`potm_stat_${s.name}`}
                    className={classes.statsItem}
                    type='single'
                    unitMeasure={getStatUnitMeasureLabel(s)}
                    label={s.displayName}
                    data={value}
                  />
                )
              );
            })}
            {enableLink && hasVideo && (
              <PkButton
                className='pk-mt--s'
                appearance='text'
                linkHref={getPlayerUrl(player.id, undefined, match?.round?.phase)}
              >
                {t(`see_player_profile${isFemaleCompetition ? '_f' : ''}`)}
                <PkIcon color='interaction' name='navigation-chevron-right' />
              </PkButton>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default withErrorBoundary(memo(PotmCard));
